import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

/**
 * PinPromptModal — reusable 4-digit PIN gate for sensitive parent actions.
 *
 * Verifies the entered PIN against `POST /api/pin/verify`. On success,
 * calls `onVerified()` (parent handler runs the actual action). On failure,
 * surfaces the upstream error message inline.
 *
 * Props:
 *   open         boolean            — controls Dialog open state
 *   onClose      () => void         — invoked when the user cancels/closes
 *   onVerified   () => Promise|void — invoked exactly once after a successful PIN verify
 *   title        string             — modal title (default: "Confirm with your PIN")
 *   description  string             — sub-copy explaining what is being authorised
 *   confirmLabel string             — CTA text (default: "Confirm")
 *   testIdPrefix string             — override for data-testid namespace
 */
export default function PinPromptModal({
  open,
  onClose,
  onVerified,
  title = "Confirm with your PIN",
  description = "Enter your 4-digit parent PIN to authorise this action.",
  confirmLabel = "Confirm",
  testIdPrefix = "pin-prompt",
}) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin(""); setErr(""); setBusy(false);
      // Autofocus once the dialog animates in
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!/^\d{4}$/.test(pin)) {
      setErr("PIN must be exactly 4 digits.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/pin/verify", { pin });
      // Parent handler runs the actual action. Any error thrown inside
      // onVerified is surfaced back to the user here.
      await onVerified?.();
      // Close after a tick so the parent gets a chance to update state
      setTimeout(() => { setBusy(false); onClose?.(); }, 0);
    } catch (e2) {
      setErr(formatApiError(e2) || "Incorrect PIN. Please try again.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid={`${testIdPrefix}-modal`}>
        <div className="relative bg-gradient-to-br from-brandSlate to-slate-800 px-6 pt-8 pb-6 text-white">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
            <ShieldCheck className="w-4 h-4" /> Parent verification
          </div>
          <DialogHeader className="relative mt-2 text-left space-y-1">
            <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">{title}</DialogTitle>
            <DialogDescription className="text-white/90 text-sm leading-relaxed">{description}</DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} className="px-6 py-6 bg-white space-y-4" noValidate>
          <div>
            <label htmlFor={`${testIdPrefix}-input`} className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
              4-digit PIN
            </label>
            <input
              ref={inputRef}
              id={`${testIdPrefix}-input`}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="input-base text-2xl tracking-[0.6em] text-center font-display"
              placeholder="••••"
              data-testid={`${testIdPrefix}-input`}
              disabled={busy}
            />
          </div>
          {err && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3 break-words" data-testid={`${testIdPrefix}-error`}>
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || pin.length !== 4}
            className="btn-primary w-full justify-center disabled:opacity-60"
            data-testid={`${testIdPrefix}-submit`}
          >
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>) : (<><KeyRound className="w-4 h-4" /> {confirmLabel}</>)}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="w-full text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1 disabled:opacity-50"
            data-testid={`${testIdPrefix}-cancel`}
          >
            Cancel
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
